import {
  AspectRatio,
  Box,
  chakra,
  Flex,
  Icon as ChakraIcon,
  Image as ChakraImage,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { buyerCurrencyFormatter } from '@diamond/shared/utils';
import { ItemInfo } from '@diamond/sol-buyer/utils';
import { CheckIconVoucher } from '@diamond/sol-buyer-components';
import { useRef } from 'react';

import { Promo } from '../label-promo';
import { LABEL_PROMO_TYPES } from '../label-promo/constants';

type CardPromoInfoBaseProps = {
  type: Promo;
  duration: string;
  text: string;
  extra?: React.ReactNode;
  isInvalid?: boolean;
  variant?: 'default' | 'checkout';
  showVoucherIcon?: boolean;
};

export interface CardPromoInfoFreeItem extends CardPromoInfoBaseProps {
  type: 'free-item';
  item: ItemInfo;
  images?: string[];
  savings?: number;
  showFreeItemPrice?: boolean;
}

export interface CardPromoInfoCashback extends CardPromoInfoBaseProps {
  type: 'cashback';
  images?: string[];
}

export interface CardPromoInfoDiscount extends CardPromoInfoBaseProps {
  type: 'discount';
  discountDisplay?: string;
  images?: string[];
}

type CardPromoInfoProps =
  | CardPromoInfoFreeItem
  | CardPromoInfoCashback
  | CardPromoInfoDiscount;

const LABEL_PROMO_DISPLAY_TYPES = {
  [LABEL_PROMO_TYPES.FREE_ITEM]: 'Gratis',
  [LABEL_PROMO_TYPES.DISCOUNT]: 'Diskon',
  [LABEL_PROMO_TYPES.CASHBACK]: 'Cashback',
};

const getLabelColor = (type: Promo, isInvalid?: boolean): string => {
  if (isInvalid) return 'gray.33';
  if (type === 'free-item' || type === 'cashback') return '#007AFF';
  return '#FF3B30';
};

const getLabelText = (type: Promo): string => {
  return LABEL_PROMO_DISPLAY_TYPES[type];
};

export function CardPromoInfo(props: CardPromoInfoProps) {
  const labelColor = getLabelColor(props.type, props.isInvalid);
  const labelText = getLabelText(props.type);
  const showFreeItemPrice =
    props.type === 'free-item'
      ? props.showFreeItemPrice !== undefined
        ? props.showFreeItemPrice
        : true
      : false;

  const gradientLine =
    'linear-gradient(58deg, rgba(255, 30, 30, 1) 18%, rgba(36, 81, 206, 1) 71%, rgba(90, 200, 255, 1) 100%)';

  const isCheckoutVariant = props.variant === 'checkout';

  if (isCheckoutVariant && props.type === 'free-item') {
    const productImageSrc =
      props.images?.[0] || props.item?.img || '/images/product_placeholder.png';
    const hasProductImage = Boolean(props.images?.[0] || props.item?.img);

    return (
      <Box
        borderRadius="8px"
        overflow="hidden"
        position="relative"
        bg="white"
        border="none"
        opacity={props.isInvalid ? 0.8 : 1}
      >
        <Flex
          display={{ base: 'none', lg: 'flex' }}
          align="center"
          gap="8px"
          px="12px"
          pt="12px"
          pb="12px"
          bgColor="transparent"
        >
          {props.showVoucherIcon && (
            <CheckIconVoucher w="18px" h="18px" flexShrink={0} />
          )}
          <Box
            p="8px"
            display="flex"
            w="fit-content"
            bgColor={labelColor}
            color={props.isInvalid ? 'gray.75' : 'white'}
            borderRadius="8px 0px 8px 0px"
            flexShrink={0}
            h="18px"
            alignItems="center"
            justifyContent="center"
          >
            <Text
              fontWeight="700"
              fontSize="9px"
              lineHeight="1.212em"
              letterSpacing="2%"
              textAlign="right"
            >
              {labelText}
            </Text>
          </Box>
          {hasProductImage && (
            <Box
              w="48px"
              h="48px"
              borderRadius="4px"
              overflow="hidden"
              flexShrink={0}
              boxShadow="0px 1px 3px rgba(0, 0, 0, 0.1)"
              bgColor="#F3F4F5"
            >
              <AspectRatio ratio={1 / 1} w="full" h="full">
                <ChakraImage
                  src={productImageSrc}
                  alt="Product Promo Image"
                  objectFit="cover"
                  w="full"
                  h="full"
                />
              </AspectRatio>
            </Box>
          )}
          <Flex direction="column" gap="2px" minW={0} flex={1}>
            <Text
              fontWeight="400"
              color="#00003D"
              fontSize="14px"
              lineHeight="1.212em"
              noOfLines={2}
              overflow="hidden"
              textOverflow="ellipsis"
            >
              {props.text}
            </Text>
            {props.savings !== undefined && props.savings > 0 && (
              <Text fontSize="12px" fontWeight="semibold" color="#FF0000">
                Hemat {buyerCurrencyFormatter(props.savings)}
              </Text>
            )}
          </Flex>
          <Text
            fontSize="10px"
            fontWeight="400"
            color="#8E8E93"
            flexShrink={0}
            whiteSpace="nowrap"
          >
            {props.duration}
          </Text>
        </Flex>

        <Flex
          display={{ base: 'flex', lg: 'none' }}
          align="center"
          gap="8px"
          p="12px"
          bgColor="transparent"
        >
          {hasProductImage && (
            <Box
              w="48px"
              h="48px"
              borderRadius="4px"
              overflow="hidden"
              flexShrink={0}
              boxShadow="0px 1px 3px rgba(0, 0, 0, 0.1)"
              bgColor="#F3F4F5"
            >
              <AspectRatio ratio={1 / 1} w="full" h="full">
                <ChakraImage
                  src={productImageSrc}
                  alt="Product Promo Image"
                  objectFit="cover"
                  w="full"
                  h="full"
                />
              </AspectRatio>
            </Box>
          )}
          <Flex direction="column" gap="2px" minW={0} flex={1}>
            <Flex align="center" gap="8px" minW={0}>
              {props.showVoucherIcon && (
                <CheckIconVoucher w="18px" h="18px" flexShrink={0} />
              )}
              <Box
                p="8px"
                display="flex"
                w="fit-content"
                bgColor={labelColor}
                color={props.isInvalid ? 'gray.75' : 'white'}
                borderRadius="8px 0px 8px 0px"
                h="18px"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
              >
                <Text
                  fontWeight="700"
                  fontSize="9px"
                  lineHeight="1.212em"
                  letterSpacing="2%"
                  textAlign="right"
                >
                  {labelText}
                </Text>
              </Box>
              <Text
                fontWeight="400"
                color="#00003D"
                fontSize="12px"
                lineHeight="1.212em"
                noOfLines={1}
                overflow="hidden"
                textOverflow="ellipsis"
                minW={0}
              >
                {props.text}
              </Text>
            </Flex>
            {props.savings !== undefined && props.savings > 0 && (
              <Text fontSize="10px" fontWeight="semibold" color="#FF0000">
                Hemat {buyerCurrencyFormatter(props.savings)}
              </Text>
            )}
          </Flex>
        </Flex>

        <Box display="none" textAlign="right" p="8px">
          <Text fontSize="10px" fontWeight="400" color="#8E8E93">
            {props.duration}
          </Text>
        </Box>

        <Box h="1px" w="full" bg={gradientLine} />
      </Box>
    );
  }

  return (
    <Box
      borderRadius="4px"
      overflow="hidden"
      position="relative"
      opacity={props.isInvalid ? 0.8 : 1}
    >
      <Box display={{ base: 'flex', lg: 'none' }} alignItems="center" gap="4px">
        {props.showVoucherIcon && (
          <CheckIconVoucher
            boxSize={{ base: '16px', lg: '20px' }}
            flexShrink={0}
          />
        )}
        <Box
          p="8px"
          display="flex"
          w="fit-content"
          bgColor={labelColor}
          color={props.isInvalid ? 'gray.75' : 'white'}
          borderRadius="8px 0px 8px 0px"
          flexShrink={0}
          h="18px"
          alignItems="center"
          justifyContent="center"
        >
          <Text
            fontWeight="700"
            fontSize="9px"
            lineHeight="1.212em"
            letterSpacing="2%"
            textAlign="right"
          >
            {labelText}
          </Text>
        </Box>
        <Text
          fontSize="10px"
          fontWeight="400"
          color="#8E8E93"
          flexShrink={0}
          whiteSpace="nowrap"
          lineHeight="1.212em"
        >
          {props.duration}
        </Text>
      </Box>

      <Flex
        align="center"
        gap="8px"
        px="8px"
        py="8px"
        pr="16px"
        bgColor={props.isInvalid ? '#F3F4F5' : '#FFFFFF'}
        borderRadius="4px"
      >
        {props.showVoucherIcon && (
          <CheckIconVoucher
            display={{ base: 'none', lg: 'block' }}
            w="18px"
            h="18px"
            flexShrink={0}
          />
        )}
        <Box
          display={{ base: 'none', lg: 'flex' }}
          px="8px"
          py="2px"
          bgColor={labelColor}
          color={props.isInvalid ? 'gray.75' : 'white'}
          borderRadius="8px 0px 8px 0px"
          flexShrink={0}
          h="18px"
          alignItems="center"
          justifyContent="center"
        >
          <Text
            fontWeight="700"
            fontSize="9px"
            lineHeight="1.212em"
            letterSpacing="2%"
            textAlign="right"
          >
            {labelText}
          </Text>
        </Box>

        <Flex flex="1" align="center" gap="4px" minW={0}>
          {props.images && props.images.length > 0 && (
            <Flex gap="4px" flexShrink={0}>
              {props.images.map((img, idx) => (
                <Box
                  key={idx}
                  w="48px"
                  h="48px"
                  borderRadius="4px"
                  overflow="hidden"
                  boxShadow="0px 1px 3px rgba(0, 0, 0, 0.1)"
                  bgColor="#F3F4F5"
                  flexShrink={0}
                >
                  <AspectRatio ratio={1 / 1} w="full" h="full">
                    <ChakraImage
                      src={img || '/images/product_placeholder.png'}
                      alt={`Product ${idx + 1}`}
                      objectFit="cover"
                      w="full"
                      h="full"
                    />
                  </AspectRatio>
                </Box>
              ))}
            </Flex>
          )}

          {!props.images && props.type === 'free-item' && props.item?.img && (
            <Box
              w="48px"
              h="48px"
              borderRadius="4px"
              overflow="hidden"
              flexShrink={0}
              boxShadow="0px 1px 3px rgba(0, 0, 0, 0.1)"
              bgColor="#F3F4F5"
            >
              <AspectRatio ratio={1 / 1} w="full" h="full">
                <ChakraImage
                  src={props.item.img || '/images/product_placeholder.png'}
                  alt="Product Promo Image"
                  objectFit="cover"
                  w="full"
                  h="full"
                />
              </AspectRatio>
            </Box>
          )}

          <Flex direction="column" gap="4px" minW={0} w="full">
            <Flex
              align="center"
              gap="4px"
              flexWrap="wrap"
              width="100%"
              justifyContent={
                props.type === 'discount' && props.discountDisplay
                  ? 'space-between'
                  : 'flex-start'
              }
            >
              <PromoText text={props.text} />
              {props.type === 'discount' && props.discountDisplay && (
                <Text
                  fontSize={{ base: '10px', lg: '12px' }}
                  color="#FF0000"
                  flexShrink={0}
                  whiteSpace="nowrap"
                  lineHeight="1.212em"
                >
                  {props.discountDisplay}
                </Text>
              )}
            </Flex>
            {props.type === 'free-item' && props.item && (
              <Flex direction="column" gap="4px">
                {showFreeItemPrice && (
                  <Flex align="center" gap="8px" flexWrap="wrap">
                    <Text
                      fontSize={{ base: '10px', lg: '12px' }}
                      fontWeight="400"
                      color="#00003D"
                    >
                      <chakra.span
                        textDecoration="line-through"
                        color="gray.75"
                        fontWeight="600"
                      >
                        {buyerCurrencyFormatter(props.item.price)}
                      </chakra.span>
                      <chakra.span fontWeight="bold" ml={2} color="#FF0000">
                        Gratis{' '}
                      </chakra.span>
                      <chakra.span fontWeight="500" color="#007AFF">
                        {props.item.qty} {props.item.uom}
                      </chakra.span>
                    </Text>
                  </Flex>
                )}
                {props.savings !== undefined && props.savings > 0 && (
                  <Text
                    fontSize={{ base: '10px', lg: '12px' }}
                    fontWeight="400"
                    color="#FF0000"
                  >
                    Hemat {buyerCurrencyFormatter(props.savings)}
                  </Text>
                )}
              </Flex>
            )}
          </Flex>
        </Flex>

        <Text
          display={{ base: 'none', lg: 'block' }}
          fontSize="10px"
          fontWeight="400"
          color="#00003D"
          flexShrink={0}
          whiteSpace="nowrap"
          lineHeight="1.212em"
        >
          {props.duration}
        </Text>

        {props.extra && <Box flexShrink={0}>{props.extra}</Box>}
      </Flex>

      <Box
        h="1px"
        w="full"
        bg={gradientLine}
        position="absolute"
        bottom={0}
        left={0}
      />
    </Box>
  );
}

const PromoText = ({ text }: { text: string }) => {
  const textRef = useRef<HTMLParagraphElement>(null);

  const isTruncated = () => {
    if (textRef.current) {
      return textRef.current.scrollHeight > textRef.current.clientHeight;
    }
    return false;
  };

  return (
    <Box flex="1" minW={0}>
      {isTruncated() ? (
        <Tooltip label={text} hasArrow>
          <Text
            ref={textRef}
            fontWeight="400"
            color="#00003D"
            fontSize={{ base: '12px', lg: '14px' }}
            lineHeight="1.212em"
            noOfLines={2}
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {text}
          </Text>
        </Tooltip>
      ) : (
        <Text
          ref={textRef}
          fontWeight="400"
          color="#00003D"
          fontSize={{ base: '12px', lg: '14px' }}
          lineHeight="1.212em"
          noOfLines={2}
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {text}
        </Text>
      )}
    </Box>
  );
};
